import React, { useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { StepIndicator } from '../components/StepIndicator';
import { UploadIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const STEPS = ['Master Creative', 'Campaign Setup', 'Select Creative', 'Styling Template', 'Variable Content', 'Generate', 'Review & Publish'];
export function VariableContent() {
  const navigate = useNavigate();
  const [pricingMode, setPricingMode] = useState<'same' | 'regional' | 'csv'>('same');
  const [offerMode, setOfferMode] = useState<'same' | 'regional' | 'csv'>('same');
  const [festivalMode, setFestivalMode] = useState<'same' | 'regional' | 'csv'>('same');
  const [pricing, setPricing] = useState('');
  const [offer, setOffer] = useState('');
  const [festival, setFestival] = useState('');
  const canContinue = pricing || offer || festival;
  return <div className="flex w-full min-h-screen bg-gray-50" data-id="element-2362">
      <Sidebar data-id="element-2363" />
      <main className="flex-1" data-id="element-2364">
        <Header data-id="element-2365" />
        <StepIndicator currentStep={5} steps={STEPS} data-id="element-2366" />
        <div className="px-6 py-8" data-id="element-2367">
          <div className="max-w-4xl mx-auto" data-id="element-2368">
            <h1 className="text-2xl font-semibold text-gray-800 mb-2" data-id="element-2369">
              Step 4: Fill Variable Content
            </h1>
            <p className="text-gray-600 mb-6" data-id="element-2370">
              Configure pricing, offers, and festival text for your campaigns
            </p>

            <div className="space-y-6" data-id="element-2371">
              {/* State-wise Pricing */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2372">
                <h3 className="font-medium text-gray-800 mb-4" data-id="element-2373">
                  A. State-wise Pricing
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-2374">
                  <button onClick={() => setPricingMode('same')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${pricingMode === 'same' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2375">
                    Same for All
                  </button>
                  <button onClick={() => setPricingMode('regional')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${pricingMode === 'regional' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2376">
                    Region-wise
                  </button>
                  <button onClick={() => setPricingMode('csv')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${pricingMode === 'csv' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2377">
                    Upload CSV
                  </button>
                </div>

                {pricingMode === 'same' && <input type="text" value={pricing} onChange={e => setPricing(e.target.value)} placeholder="e.g., Starting at ₹12,50,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2378" />}

                {pricingMode === 'regional' && <div className="space-y-3" data-id="element-2379">
                    <div className="grid grid-cols-2 gap-3" data-id="element-2380">
                      <div data-id="element-2381">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2382">
                          North Zone
                        </label>
                        <input type="text" placeholder="₹12,50,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2383" />
                      </div>
                      <div data-id="element-2384">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2385">
                          South Zone
                        </label>
                        <input type="text" placeholder="₹12,75,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2386" />
                      </div>
                      <div data-id="element-2387">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2388">
                          East Zone
                        </label>
                        <input type="text" placeholder="₹12,60,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2389" />
                      </div>
                      <div data-id="element-2390">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2391">
                          West Zone
                        </label>
                        <input type="text" placeholder="₹12,80,000" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2392" />
                      </div>
                    </div>
                  </div>}

                {pricingMode === 'csv' && <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-2393">
                    <UploadIcon className="mx-auto text-gray-400 mb-3" size={32} data-id="element-2394" />
                    <p className="text-sm text-gray-600 mb-3" data-id="element-2395">
                      Upload CSV with dealer-specific pricing
                    </p>
                    <label className="inline-block bg-blue-500 text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-blue-600" data-id="element-2396">
                      Choose CSV File
                      <input type="file" accept=".csv" className="hidden" data-id="element-2397" />
                    </label>
                    <p className="text-xs text-gray-500 mt-2" data-id="element-2398">
                      Format: dealer_id, pricing
                    </p>
                  </div>}
              </div>

              {/* Offer/Discount Text */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2399">
                <h3 className="font-medium text-gray-800 mb-4" data-id="element-2400">
                  B. Offer/Discount Text
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-2401">
                  <button onClick={() => setOfferMode('same')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${offerMode === 'same' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2402">
                    Same for All
                  </button>
                  <button onClick={() => setOfferMode('regional')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${offerMode === 'regional' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2403">
                    Region-wise
                  </button>
                  <button onClick={() => setOfferMode('csv')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${offerMode === 'csv' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2404">
                    Dealer-specific CSV
                  </button>
                </div>

                {offerMode === 'same' && <textarea value={offer} onChange={e => setOffer(e.target.value)} placeholder="e.g., Get ₹50,000 off + Free accessories worth ₹25,000" rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2405" />}

                {offerMode === 'regional' && <div className="space-y-3" data-id="element-2406">
                    {['North', 'South', 'East', 'West'].map(zone => <div key={zone} data-id="element-2407">
                        <label className="block text-sm text-gray-600 mb-1" data-id="element-2408">
                          {zone} Zone Offer
                        </label>
                        <input type="text" placeholder={`Special offer for ${zone} zone`} className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2409" />
                      </div>)}
                  </div>}

                {offerMode === 'csv' && <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-2410">
                    <UploadIcon className="mx-auto text-gray-400 mb-3" size={32} data-id="element-2411" />
                    <p className="text-sm text-gray-600 mb-3" data-id="element-2412">
                      Upload CSV with dealer-specific offers
                    </p>
                    <label className="inline-block bg-blue-500 text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-blue-600" data-id="element-2413">
                      Choose CSV File
                      <input type="file" accept=".csv" className="hidden" data-id="element-2414" />
                    </label>
                  </div>}
              </div>

              {/* Festival Text */}
              <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2415">
                <h3 className="font-medium text-gray-800 mb-4" data-id="element-2416">
                  C. Festival Text
                </h3>
                <div className="flex space-x-3 mb-4" data-id="element-2417">
                  <button onClick={() => setFestivalMode('same')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${festivalMode === 'same' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2418">
                    Same for All
                  </button>
                  <button onClick={() => setFestivalMode('regional')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${festivalMode === 'regional' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2419">
                    Regional Mapping
                  </button>
                  <button onClick={() => setFestivalMode('csv')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${festivalMode === 'csv' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-2420">
                    Dealer-specific CSV
                  </button>
                </div>

                {festivalMode === 'same' && <input type="text" value={festival} onChange={e => setFestival(e.target.value)} placeholder="e.g., Diwali Special Offer" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2421" />}

                {festivalMode === 'regional' && <div className="space-y-3" data-id="element-2422">
                    <div data-id="element-2423">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-2424">
                        North India
                      </label>
                      <input type="text" placeholder="e.g., Diwali Dhamaka" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2425" />
                    </div>
                    <div data-id="element-2426">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-2427">
                        South India
                      </label>
                      <input type="text" placeholder="e.g., Pongal Special" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2428" />
                    </div>
                    <div data-id="element-2429">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-2430">
                        East India
                      </label>
                      <input type="text" placeholder="e.g., Durga Puja Offer" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2431" />
                    </div>
                    <div data-id="element-2432">
                      <label className="block text-sm text-gray-600 mb-1" data-id="element-2433">
                        West India
                      </label>
                      <input type="text" placeholder="e.g., Ganesh Chaturthi Special" className="w-full px-4 py-2 border border-gray-300 rounded-md" data-id="element-2434" />
                    </div>
                  </div>}

                {festivalMode === 'csv' && <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-id="element-2435">
                    <UploadIcon className="mx-auto text-gray-400 mb-3" size={32} data-id="element-2436" />
                    <p className="text-sm text-gray-600 mb-3" data-id="element-2437">
                      Upload CSV with dealer-specific festival text
                    </p>
                    <label className="inline-block bg-blue-500 text-white px-4 py-2 rounded-md text-sm cursor-pointer hover:bg-blue-600" data-id="element-2438">
                      Choose CSV File
                      <input type="file" accept=".csv" className="hidden" data-id="element-2439" />
                    </label>
                  </div>}
              </div>
            </div>

            <div className="flex justify-between mt-6" data-id="element-2440">
              <button onClick={() => navigate('/dealerization/step-3')} className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-2441">
                Back
              </button>
              <button onClick={() => navigate('/dealerization/step-5')} disabled={!canContinue} className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-2442">
                Continue to Generate Creatives
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>;
}